import React, { useState } from "react";
import { FiClock, FiX, FiPlus } from "react-icons/fi";
import { AssetRecordType } from "tldraw";

import { useAuth } from "../../contexts/AuthContext.jsx";
import { useAppContext } from "../../contexts/AppContext.jsx";
import { useUserGenerations } from "../../hooks/useApi";
import { TaskStatus } from "../../constants/taskStatus";

export function GenerationsToggle({ isOpen, setIsOpen }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        backgroundColor: isOpen ? "#000" : "#f5f5f5",
        color: isOpen ? "#fff" : "#555",
        border: "none",
        borderRadius: "10px",
        cursor: "pointer",
        transition: "all 0.15s ease",
        flexShrink: 0,
      }}
    >
      {isOpen ? <FiX size={15} /> : <FiClock size={15} />}
    </button>
  );
}

export function GenerationsPopup({ isOpen }) {
  const { user } = useAuth();
  const { editor } = useAppContext();
  const { data, isLoading } = useUserGenerations("0", 20);

  const generations = data?.items || [];
  const completedGenerations = generations.filter(
    (g) => g.status === TaskStatus.COMPLETE && g.url
  );

  const handleAddToCanvas = (generation) => {
    if (!editor || !generation.url) return;

    const img = new Image();
    img.onload = () => {
      let w = img.width;
      let h = img.height;
      const maxSize = 400;
      if (w > maxSize || h > maxSize) {
        if (w > h) {
          h = (h / w) * maxSize;
          w = maxSize;
        } else {
          w = (w / h) * maxSize;
          h = maxSize;
        }
      }

      const viewportBounds = editor.getViewportPageBounds();
      const x = viewportBounds.x + (viewportBounds.w - w) / 2;
      const y = viewportBounds.y + (viewportBounds.h - h) / 2;

      const assetId = AssetRecordType.createId();
      editor.createAssets([
        {
          id: assetId,
          type: "image",
          typeName: "asset",
          props: {
            name: `generation-${generation.id}.png`,
            src: generation.url,
            w: img.width,
            h: img.height,
            mimeType: "image/png",
            isAnimated: false,
          },
          meta: {},
        },
      ]);

      editor.createShape({
        type: "image",
        x,
        y,
        props: { assetId, w, h },
      });
    };
    img.src = generation.url;
  };

  if (!user || !isOpen) return null;

  return (
    <div
      style={{
        position: "absolute",
        bottom: "100%",
        right: 0,
        marginBottom: 8,
        width: 280,
        maxHeight: 400,
        backgroundColor: "#fff",
        border: "1px solid #eee",
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
          fontSize: "13px",
          fontWeight: 600,
          color: "#333",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <FiClock size={14} />
        Recent Generations
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "8px",
        }}
      >
        {isLoading ? (
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              fontSize: "13px",
              color: "#999",
            }}
          >
            Loading...
          </div>
        ) : completedGenerations.length === 0 ? (
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              fontSize: "13px",
              color: "#999",
            }}
          >
            No generations yet
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "6px",
            }}
          >
            {completedGenerations.map((gen) => (
              <div
                key={gen.id}
                style={{
                  position: "relative",
                  aspectRatio: "1",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #f0f0f0",
                  cursor: "pointer",
                  transition: "border-color 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#ccc";
                  e.currentTarget.querySelector(".add-overlay").style.opacity = 1;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#f0f0f0";
                  e.currentTarget.querySelector(".add-overlay").style.opacity = 0;
                }}
                onClick={() => handleAddToCanvas(gen)}
              >
                <img
                  src={gen.url}
                  alt="Generation"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                <div
                  className="add-overlay"
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    opacity: 0,
                    transition: "opacity 0.15s",
                  }}
                >
                  <FiPlus size={20} color="#fff" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default GenerationsPopup;
